import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { groups as groupsApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Users, Plus, Loader2, DollarSign } from 'lucide-react';

// Public directory of every group. Anyone can browse; auth users can
// create a new group (which auto-registers them as an affiliate).
export default function Groups() {
  const [rows, setRows] = useState(null);
  const [modal, setModal] = useState(null); // { name, description, membershipFeeUsd }
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setRows(await groupsApi.list()); }
    catch { setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!modal?.name?.trim()) { toast({ title: 'Give your group a name' }); return; }
    setSaving(true);
    try {
      const g = await groupsApi.create({
        name: modal.name.trim(),
        description: modal.description || '',
        membershipFeeUsd: Number(modal.membershipFeeUsd || 0),
        membershipRequired: Number(modal.membershipFeeUsd || 0) > 0,
      });
      toast({ title: 'Group created — you are now an affiliate!', description: g.name });
      setModal(null);
      window.location.href = `/groups/mine`;
    } catch (e) { toast({ title: 'Create failed', description: e?.response?.data?.detail || '' }); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="groups-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Users size={22}/> Groups</h1>
          <p className="text-sm text-gray-400 mt-1">Communities inside PBB. Every group organizer earns 10% commission on their members' tier subscriptions and membership fees.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/groups/mine" data-testid="groups-my-organized-link"
                className="text-sm px-3 py-1.5 rounded border border-gray-700 text-white hover:bg-gray-800">My groups</Link>
          <button data-testid="groups-create-open"
                  onClick={() => setModal({ name: '', description: '', membershipFeeUsd: 0 })}
                  className="pbb-btn-green inline-flex items-center gap-1 text-sm">
            <Plus size={14}/> Create group
          </button>
        </div>
      </div>

      {rows === null ? (
        <div className="text-gray-400 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-gray-500 text-sm border border-dashed border-gray-800 rounded-lg p-6 text-center">
          No groups yet. Be the first — create one above and start building.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map(g => (
            <Link to={`/groups/${g.slug}`} key={g.id} data-testid={`groups-card-${g.id}`}
                  className="bg-gray-900 border border-gray-800 rounded-lg p-4 hover:border-green-500 transition-colors">
              {g.coverImage && <img src={g.coverImage} alt="" className="w-full h-32 object-cover rounded mb-3"/>}
              <div className="font-semibold text-white">{g.name}</div>
              <div className="text-xs text-gray-400 line-clamp-2 mt-1">{g.description || 'No description yet.'}</div>
              <div className="flex items-center justify-between mt-3 text-xs">
                <span className="text-gray-500 inline-flex items-center gap-1">
                  <Users size={12}/> {g.memberCount || 0} member{g.memberCount === 1 ? '' : 's'}
                </span>
                {Number(g.membershipFeeUsd) > 0 && (
                  <span className="text-green-400 inline-flex items-center gap-0.5">
                    <DollarSign size={11}/>{Number(g.membershipFeeUsd).toFixed(0)}/yr
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center px-4"
             data-testid="groups-create-modal">
          <div className="w-full max-w-md bg-[#0f0f0f] border border-gray-800 rounded-2xl p-5 space-y-3">
            <div className="text-white font-semibold flex items-center gap-2"><Plus size={16}/> New group</div>
            <input data-testid="groups-create-name" placeholder="Group name"
                   value={modal.name} onChange={e => setModal({ ...modal, name: e.target.value })}
                   className="w-full bg-black border border-gray-700 rounded px-3 py-2 text-white text-sm"/>
            <textarea data-testid="groups-create-desc" placeholder="What's this group about?" rows={3}
                      value={modal.description} onChange={e => setModal({ ...modal, description: e.target.value })}
                      className="w-full bg-black border border-gray-700 rounded px-3 py-2 text-white text-sm"/>
            <label className="block text-xs text-gray-400">
              Optional yearly membership fee (USD)
              <input data-testid="groups-create-fee" type="number" min={0} step={1}
                     value={modal.membershipFeeUsd} onChange={e => setModal({ ...modal, membershipFeeUsd: e.target.value })}
                     className="mt-1 w-full bg-black border border-gray-700 rounded px-3 py-2 text-white text-sm"/>
              <div className="text-[11px] text-gray-500 mt-1">Platform takes 10% via Stripe. You can add this later.</div>
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setModal(null)} className="px-3 py-2 rounded border border-gray-700 text-gray-300 text-sm">Cancel</button>
              <button data-testid="groups-create-submit" onClick={create} disabled={saving}
                      className="pbb-btn-green text-sm inline-flex items-center gap-1 disabled:opacity-60">
                {saving ? <Loader2 size={12} className="animate-spin"/> : <Plus size={12}/>} Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
